import type { TranscriptSpeakerPayload } from '@Application/Beneficiaries/Transcripts/Actions/transcriptSpeakersFetched';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';

const UNASSIGNED_KIND = 'unassigned';
const ROLE_KIND = 'role';
const BENEFICIARY_KIND = 'beneficiary';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `contactId` is read from `payload.contact?.id ?? null`: it only checks the right candidate in the
 * assignment panel, it is never used to display a name (§8.2). `kind` is wrapped in `LabeledValueView`,
 * never read as a bare string.
 */
class TranscriptSpeakerView {
	readonly id: number;
	readonly label: string;
	readonly kind: LabeledValueView;
	readonly displayName: string | null;
	readonly contactId: number | null;
	readonly turnCount: number;
	readonly sampleText: string | null;
	readonly roleKey: string | null;
	readonly roleLabel: string | null;

	constructor(payload: TranscriptSpeakerPayload) {
		this.id = payload.id;
		this.label = payload.label;
		this.kind = new LabeledValueView(payload.kind);
		this.displayName = payload.displayName;
		this.contactId = payload.contact?.id ?? null;
		this.turnCount = payload.turnCount;
		this.sampleText = payload.sampleText;
		this.roleKey = payload.roleKey ?? null;
		this.roleLabel = payload.roleLabel ?? null;

		Object.freeze(this);
	}

	get name(): string {
		return this.displayName ?? this.roleLabel ?? this.label;
	}

	/** The role label of a role speaker, otherwise the label of its kind. */
	get roleDisplayLabel(): string {
		if (this.kind.value === ROLE_KIND && this.roleLabel !== null) {
			return this.roleLabel;
		}

		return this.kind.label;
	}

	get isAssigned(): boolean {
		return this.kind.value !== UNASSIGNED_KIND;
	}

	get provenanceLabel(): string {
		if (this.kind.value === BENEFICIARY_KIND) {
			return 'Dit par la personne';
		}

		if (this.kind.value === UNASSIGNED_KIND) {
			return 'Non attribué';
		}

		return 'Dit par un tiers';
	}
}

export default TranscriptSpeakerView;
